/**
 * Query scope for the Consultation no-show tab (BAR-2809): each patient's
 * `no-show-consult` (BAR-2888), so it pages with one record per patient.
 */

import { Appointment, Meta } from '@medplum/fhirtypes';
import {
  appointmentTypeTokens,
  CONSULTATION_APPOINTMENT_CODES,
  NO_SHOW_CONSULT_TAG_CODE,
  NO_SHOW_CONSULT_TAG_SYSTEM,
  PATIENT_FILTER_TAG_SYSTEMS,
} from '@bariendo/lib/constants';
import { ListAppointment } from '@bariendo/lib/pac-list-appointments';
import { ListRange } from '@/components/Hub/listRange';
import {
  appointmentQueueSubscriptionParams,
  buildAppointmentQueueFilter,
  isInAppointmentQueueScope,
} from './appointmentQueueScope';
import { QueueFilterValues, withPatientFilters } from './queueFilters';

const CONSULT_TYPE_TOKENS = appointmentTypeTokens(CONSULTATION_APPOINTMENT_CODES);

const MISSED_STATUSES: readonly Appointment['status'][] = ['noshow', 'cancelled'];

const NO_SHOW_CONSULT_TAG_CLAUSE = `_tag eq "${NO_SHOW_CONSULT_TAG_SYSTEM}|${NO_SHOW_CONSULT_TAG_CODE}"`;

/** The `hub-queue` code the sync gives a patient listed on this tab. */
const NO_SHOW_QUEUE_CODE = 'no-show';

/** A consult as the query returns it: its participants carry the patient's tags. */
type NoShowConsult = ListAppointment & {
  meta?: Meta;
  participant?: { actor?: { resource?: { resourceType?: string; meta?: Meta } } }[];
};

function hasNoShowConsultTag(appointment: { meta?: Meta }): boolean {
  return Boolean(
    appointment.meta?.tag?.some(
      (tag) => tag.system === NO_SHOW_CONSULT_TAG_SYSTEM && tag.code === NO_SHOW_CONSULT_TAG_CODE
    )
  );
}

function isQueuedNoShow(patient: { meta?: Meta } | undefined): boolean {
  return Boolean(
    patient?.meta?.tag?.some(
      (tag) => tag.system === PATIENT_FILTER_TAG_SYSTEMS.queue && tag.code === NO_SHOW_QUEUE_CODE
    )
  );
}

/**
 * The patient's latest missed consult, in range, for patients the sync queued on
 * No-show, narrowed by the hub's filters (BAR-2885). The consult tag alone also
 * marks patients outside the hub's stages.
 */
export function buildNoShowFilter(range: ListRange, values: QueueFilterValues): string {
  const missed = buildAppointmentQueueFilter(CONSULT_TYPE_TOKENS, range, { statuses: MISSED_STATUSES });
  const queued = `patient._tag eq "${PATIENT_FILTER_TAG_SYSTEMS.queue}|${NO_SHOW_QUEUE_CODE}"`;
  return withPatientFilters(`(${missed} and ${NO_SHOW_CONSULT_TAG_CLAUSE} and ${queued})`, values);
}

export function noShowSubscriptionParams(): Record<string, string> {
  return appointmentQueueSubscriptionParams(CONSULT_TYPE_TOKENS);
}

/**
 * The query's scope, for what live updates add to a page: rows that arrive by
 * subscription skip `buildNoShowFilter`, so both tags are checked here too — the
 * consult's, and the patient's queue tag, which keeps out Healthie imports and
 * patients past their procedure.
 */
export function isInNoShowScope(consult: NoShowConsult, range: ListRange): boolean {
  const patient = consult.participant
    ?.map((participant) => participant.actor?.resource)
    .find((resource) => resource?.resourceType === 'Patient');
  return (
    hasNoShowConsultTag(consult) &&
    isQueuedNoShow(patient) &&
    isInAppointmentQueueScope(consult, range, { statuses: MISSED_STATUSES })
  );
}
